import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebaseConfig';
import { withAdminSession } from './adminAuth';

export const DEFAULT_SHOP_USER_ID = '10467';
const STORAGE_KEY = 'tianlanCurrentShop';
const JAMBO_PROXY_URL = process.env.REACT_APP_JAMBO_PROXY_URL || '/api/jambo';
const JAMBO_PROXY_FALLBACK_URL = process.env.REACT_APP_JAMBO_PROXY_FALLBACK_URL || 'https://us-central1-barcode-1b39e.cloudfunctions.net/proxyJamboAPI';

export const getStoredShop = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      shopUserId: stored.shopUserId || DEFAULT_SHOP_USER_ID,
      storeId: stored.storeId || stored.shopUserId || DEFAULT_SHOP_USER_ID,
      storeName: stored.storeName || `直播商店 ${stored.shopUserId || DEFAULT_SHOP_USER_ID}`,
    };
  } catch (error) {
    return {
      shopUserId: DEFAULT_SHOP_USER_ID,
      storeId: DEFAULT_SHOP_USER_ID,
      storeName: `直播商店 ${DEFAULT_SHOP_USER_ID}`,
    };
  }
};

export const setStoredShop = (shop) => {
  const nextShop = {
    shopUserId: shop.shopUserId || DEFAULT_SHOP_USER_ID,
    storeId: shop.storeId || shop.shopUserId || DEFAULT_SHOP_USER_ID,
    storeName: shop.storeName || `直播商店 ${shop.shopUserId || DEFAULT_SHOP_USER_ID}`,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(nextShop));
  window.dispatchEvent(new CustomEvent('tianlanStoreChanged', { detail: nextShop }));
  return nextShop;
};

export const useTianlanStore = () => {
  const [shop, setShop] = useState(getStoredShop);

  useEffect(() => {
    const handleChange = (event) => {
      setShop(event.detail || getStoredShop());
    };
    window.addEventListener('tianlanStoreChanged', handleChange);
    window.addEventListener('storage', handleChange);
    return () => {
      window.removeEventListener('tianlanStoreChanged', handleChange);
      window.removeEventListener('storage', handleChange);
    };
  }, []);

  return shop;
};

export const withShopScope = (data = {}, shop = getStoredShop()) => ({
  ...data,
  shopUserId: shop.shopUserId,
  shop_user_id: shop.shopUserId,
  storeId: shop.storeId || shop.shopUserId,
  storeName: shop.storeName || `直播商店 ${shop.shopUserId}`,
});

export const getScopedSettingsDocId = (baseId, shop = getStoredShop()) => {
  const shopUserId = shop.shopUserId || DEFAULT_SHOP_USER_ID;
  return shopUserId === DEFAULT_SHOP_USER_ID ? baseId : `${baseId}_${shopUserId}`;
};

export const callJamboProxy = async (action, payload = {}, shop = getStoredShop()) => {
  const requestPayload = withAdminSession(withShopScope({ action, ...payload }, shop));
  const callHttpProxy = async (url) => {
    const idToken = await auth.currentUser?.getIdToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(requestPayload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `直播訂單服務連線失敗 HTTP ${response.status}`);
    }
    return data;
  };
  const callCallableProxy = async (fallbackReason = '') => {
    const proxy = httpsCallable(functions, 'tianlanProxyJamboAPI');
    const result = await proxy(requestPayload);
    const data = result.data || {};
    if (data.success === false) {
      throw new Error(data.error || fallbackReason || '直播訂單服務連線失敗');
    }
    return data;
  };

  try {
    return await callHttpProxy(JAMBO_PROXY_URL);
  } catch (error) {
    const shouldFallback =
      JAMBO_PROXY_URL === '/api/jambo' &&
      /(HTTP 404|HTTP 500|HTTP 502|HTTP 503|HTTP 504|Failed to fetch|NetworkError)/i.test(error.message || '');
    if (!shouldFallback) throw error;
    try {
      return await callHttpProxy(JAMBO_PROXY_FALLBACK_URL);
    } catch (fallbackError) {
      return callCallableProxy(fallbackError.message || error.message);
    }
  }
};

export const isInCurrentShop = (data, shop = getStoredShop()) => {
  const docShopId = data?.shopUserId || data?.storeId;
  return !docShopId || docShopId === shop.shopUserId || docShopId === shop.storeId;
};
